import { type CompanyGroup } from "~/api/types";

/** Uppercase letters of the name, e.g. "Handelsbanken Finans AB" -> "HFAB". */
export const getGroupInitials = (name: string): string => {
  const initials =
    name.match(/\p{Lu}/gu)?.join("") ?? name[0]?.toUpperCase() ?? "";
  return initials.slice(0, 4);
};

/** Ancestors of `startId`, ordered root-first. Excludes the group itself. */
export const getAncestorGroups = (
  groups: CompanyGroup[],
  startId: number,
): CompanyGroup[] => {
  const byId = new Map(groups.map((group) => [group.id, group]));
  const ancestors: CompanyGroup[] = [];

  let current = byId.get(startId);
  while (current && current.parentCompanyGroupId !== null) {
    const parent = byId.get(current.parentCompanyGroupId);
    if (!parent || ancestors.includes(parent)) break;
    ancestors.unshift(parent);
    current = parent;
  }

  return ancestors;
};

/** Descendants of `rootId` in depth-first tree order. Excludes the root. */
export const getDescendantGroups = (
  groups: CompanyGroup[],
  rootId: number,
): CompanyGroup[] => {
  const childrenOf = new Map<number, CompanyGroup[]>();
  for (const group of groups) {
    if (group.parentCompanyGroupId === null) continue;
    const siblings = childrenOf.get(group.parentCompanyGroupId) ?? [];
    siblings.push(group);
    childrenOf.set(group.parentCompanyGroupId, siblings);
  }

  const descendants: CompanyGroup[] = [];
  const walk = (id: number) => {
    for (const child of childrenOf.get(id) ?? []) {
      descendants.push(child);
      walk(child.id);
    }
  };
  walk(rootId);

  return descendants;
};
